// The Wi-Fi networks already in use at a location, WITH their passwords — so
// adding a second network to a device is a tap rather than a retyped password
// that has to be right first time.
//
// TWO SOURCES, because neither alone is complete:
//
//   1. The location's registered networks (GetAllWifiNetworksByLocationId).
//      Would be the authority. ⚠️ CURRENTLY BROKEN SERVER-SIDE: the route
//      exists but never responds, for valid location ids as well as junk ones.
//      Flutter hits the same endpoint and hangs the same way. It stays wired up
//      so this starts working the moment the server is fixed, but nothing may
//      depend on it answering.
//
//   2. Asking the location's devices directly. `getallwifi` makes a device
//      report every network it holds, ssid and password, on its /client topic.
//      That is exactly what the server stores anyway
//      (ProcessRefreshAllWifiNetworks parses this same reply), so it is the same
//      data one hop earlier — and it works today.
//
// Source 2 only reaches devices that are online. That is a real limit, not a
// bug to paper over: callers are told which source each network came from so
// the UI can say what it actually knows.

import { getLocationWifi, getDevicesByLocation } from '../api/api';
import { connectMqtt, subscribeTopic, addMessageListener, publishMessage } from '../mqtt/mqttClient';
import { CMD_GET_ALL_WIFI, parseWifiList } from '../devices/commands';

// How long to listen for `getallwifi` replies. A device answers in well under a
// second when it is online; the rest of the wait is only for stragglers, and a
// picker that makes you wait longer than this to see a list is not a shortcut.
export const REPLY_WINDOW_MS = 2500;

// Devices asked per location. Enough to cover a whole home, low enough that a
// large site does not fire a hundred publishes to fill one dialog.
export const MAX_DEVICES_ASKED = 25;

const unoOf = (d) => String(d?.UniqueNumber ?? '');

// Ask every device at this location what networks it holds, and collect the
// replies that arrive within the window.
async function askDevices(locationId) {
  const res = await getDevicesByLocation(locationId).catch(() => null);
  const unos = (Array.isArray(res?.data) ? res.data : [])
    .map(unoOf)
    .filter(Boolean)
    .slice(0, MAX_DEVICES_ASKED);
  if (!unos.length) return [];

  connectMqtt();
  const wanted = new Set(unos);
  const found = [];

  // Listen BEFORE publishing. A device can answer in milliseconds, and a
  // listener attached afterwards misses exactly the fastest replies.
  const detach = addMessageListener((topic, payload) => {
    const slash = String(topic).indexOf('/');
    const uno = slash === -1 ? String(topic) : String(topic).slice(0, slash);
    if (!wanted.has(uno) || !String(topic).endsWith('/client')) return;
    // Strip the `[uno]` envelope the device wraps its reply in.
    parseWifiList(String(payload).replace(/^\[[^\]]*\]/, '')).forEach((w) => {
      if (w.ssid) found.push({ ssid: w.ssid, pass: w.pass || '', from: 'device', uno });
    });
  });

  unos.forEach((uno) => {
    subscribeTopic(`${uno}/client`);
    // Raw, no [uno]…#userId envelope — this is how the Flutter menu asks, and
    // the firmware answers either form.
    publishMessage(`${uno}/device`, CMD_GET_ALL_WIFI);
  });

  await new Promise((r) => { setTimeout(r, REPLY_WINDOW_MS); });
  detach();
  return found;
}

/**
 * Every network known for a location.
 *
 * Returns { networks, serverFailed, askedDevices } — `serverFailed` matters:
 * an empty list because the server hung is not the same claim as "this location
 * has no networks", and the UI must not conflate them.
 */
export async function getKnownNetworks(locationId) {
  if (locationId == null) return { networks: [], serverFailed: false, askedDevices: false };

  const [dbRows, fromDevices] = await Promise.all([
    getLocationWifi(locationId).then((r) => (Array.isArray(r?.data) ? r.data : [])).catch(() => null),
    askDevices(locationId),
  ]);

  const registered = (dbRows || [])
    .map((w) => ({ ssid: w.WifiName ?? '', pass: w.WifiPassword ?? '', from: 'location' }))
    .filter((w) => w.ssid);

  // Registered first: where both know an SSID, the stored password is the one
  // someone deliberately recorded for the site.
  const bySsid = new Map();
  [...registered, ...fromDevices].forEach((w) => {
    if (!w.ssid) return;
    const prev = bySsid.get(w.ssid);
    // Prefer whichever entry actually carries a password.
    if (!prev || (!prev.pass && w.pass)) bySsid.set(w.ssid, w);
  });

  return {
    networks: [...bySsid.values()],
    serverFailed: dbRows === null,
    askedDevices: fromDevices.length > 0,
  };
}
